/**
 * Dynamic Lalamove delivery-options pickers, migrated verbatim from the
 * retired credential-sets key-editing modal (Integrations Hub P3,
 * 2026-07-30) into the IntegrationDrawer's edit mode.
 */
import type { ServiceOptions } from '@/hooks/useDeliveryOptions'

/** Vehicle-class select fed by the live Lalamove catalog. Changing vehicle
 *  also prunes the special_requests form value to the new vehicle's valid
 *  names — special requests are per vehicle (MX MOTORCYCLE has no
 *  door-to-door), and a stale one 422s at quote time. */
export function VehicleSelect({ fieldId, label, services, value, selectedRequests, onChange }: {
  fieldId: string
  label: string
  services: ServiceOptions[]
  value: string
  selectedRequests: string[]
  onChange: (vehicle: string, prunedRequests: string[]) => void
}) {
  return (
    <div>
      <label htmlFor={fieldId} className="block text-sm font-medium text-foreground mb-1">
        {label}
      </label>
      <select
        id={fieldId}
        value={value}
        onChange={(e) => {
          const vehicle = e.target.value
          const valid = new Set(
            services.find((s) => s.key === vehicle)?.special_requests.map((r) => r.name) ?? [],
          )
          onChange(vehicle, selectedRequests.filter((r) => valid.has(r)))
        }}
        className="w-full text-sm rounded-md border border-border bg-card px-3 py-2 text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring"
      >
        <option value="">Default (HATCHBACK)</option>
        {services.map((s) => (
          <option key={s.key} value={s.key}>
            {s.key} — {s.load_value}{s.load_unit}
          </option>
        ))}
      </select>
    </div>
  )
}

/** Special-request checkboxes scoped to the selected vehicle. Persisted
 *  values not offered for this vehicle stay visible (checked, flagged) so a
 *  save can clear them — never silently dropped on render. `max_selection`
 *  caps checked boxes per parent_type group. */
export function SpecialRequestChecklist({ fieldId, label, service, selected, onChange }: {
  fieldId: string
  label: string
  service: ServiceOptions | undefined
  selected: string[]
  onChange: (requests: string[]) => void
}) {
  const offered = service?.special_requests ?? []
  const offeredNames = new Set(offered.map((r) => r.name))
  const stale = selected.filter((name) => !offeredNames.has(name))
  const checkedInGroup = (parent: string) =>
    offered.filter((r) => r.parent_type === parent && selected.includes(r.name)).length

  const toggle = (name: string) =>
    onChange(selected.includes(name)
      ? selected.filter((n) => n !== name)
      : [...selected, name])

  return (
    <fieldset id={fieldId} aria-label={label}>
      <legend className="block text-sm font-medium text-foreground mb-1">{label}</legend>
      <div className="space-y-1">
        {offered.map((r) => {
          const checked = selected.includes(r.name)
          const groupFull = r.max_selection !== null
            && checkedInGroup(r.parent_type) >= r.max_selection
          return (
            <label key={r.name} className="flex items-start gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={checked}
                disabled={!checked && groupFull}
                onChange={() => toggle(r.name)}
                aria-label={r.description || r.name}
              />
              <span>
                {r.description || r.name}
                <span className="text-muted-foreground"> ({r.name})</span>
              </span>
            </label>
          )
        })}
        {stale.map((name) => (
          <label key={name} className="flex items-start gap-2 text-sm text-warning">
            <input
              type="checkbox"
              className="mt-0.5"
              checked
              onChange={() => toggle(name)}
              aria-label={name}
            />
            <span>{name} — not offered for this vehicle; uncheck and save to clear</span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}
